import type { SignupProfile, CompanySize } from '@shared/signup';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, passwordLinkContext, databaseFetch } from '../supabase';
import { passwordRedirect, type PasswordLink } from '../auth-links';
import { getAttribution } from '../attribution';
import { config } from '../config';
import { detectSignupCountry } from '../signup-country';
import type { AuthUser } from '../types';

export function toAuthUser(user: User): AuthUser {
  const meta = (user.user_metadata || {}) as Record<string, unknown>;
  const provider = String(user.app_metadata?.provider || 'email');
  return {
    id: user.id,
    email: user.email || '',
    name: String(meta.full_name || meta.name || (user.email ? user.email.split('@')[0] : 'there')),
    phone: String(meta.phone || ''),
    provider,
    emailVerified: Boolean(user.email_confirmed_at) || provider === 'google',
    createdAt: user.created_at || null,
    metadata: meta,
  };
}

let sessionRequest: Promise<Session | null> | null = null;

/** Coalesce StrictMode, focus and token events into one verified session request. */
export function getSession(): Promise<Session | null> {
  if (!sessionRequest)
    sessionRequest = readVerifiedSession().finally(() => {
      sessionRequest = null;
    });
  return sessionRequest;
}

async function readVerifiedSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  if (!data.session) return null;
  // Local storage can retain an unexpired JWT after an administrator deletes the user.
  const { data: verified, error } = await supabase.auth.getUser();
  if (error) {
    if (
      ![401, 403].includes(error.status || 0) &&
      !['user_not_found', 'session_not_found'].includes(error.code || '')
    )
      throw error;
    await supabase.auth.signOut({ scope: 'local' });
    return null;
  }
  if (!verified.user || verified.user.id !== data.session.user.id) {
    await supabase.auth.signOut({ scope: 'local' });
    return null;
  }
  data.session.user = verified.user;
  // Hosted Google OAuth returns after a page reload, without the ID-token callback.
  if (data.session?.user.app_metadata?.provider === 'google') {
    try {
      const user = await saveGoogleAttribution(data.session.user);
      if (user) data.session.user = user;
    } catch {
      // A metadata failure must not prevent login; the next load can retry.
    }
  }
  return data.session;
}

async function saveGoogleAttribution(user: User): Promise<User | null> {
  const createdAt = new Date(user.created_at).getTime();
  if (!Number.isFinite(createdAt) || Date.now() - createdAt > 60_000) return null;
  const { referral, source, attribution: campaign } = getAttribution();
  const metadata = user.user_metadata || {};
  const attribution = {
    ...(referral && !metadata.referral ? { referral } : {}),
    ...(source && !metadata.source ? { source } : {}),
    ...(campaign && !metadata.attribution ? { attribution: campaign } : {}),
  };
  if (!Object.keys(attribution).length) return null;
  const { data, error } = await supabase.auth.updateUser({ data: attribution });
  if (error) throw error;
  return data.user;
}

export function onAuthChange(callback: (event: string, session: Session | null) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((event, session) => callback(event, session));
  return () => data.subscription.unsubscribe();
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) {
    if (/invalid login credentials/i.test(error.message))
      throw new Error('That email and password don’t match. Try again or reset your password.');
    if (/email not confirmed/i.test(error.message)) {
      const e = new Error('Please confirm your email first — check your inbox for the link.');
      (e as Error & { code?: string }).code = 'email_not_confirmed';
      throw e;
    }
    throw error;
  }
}

export interface SignUpInput {
  email: string;
  name: string;
  company: string;
  companySize: CompanySize;
  phone: string;
  whatsappNotifications?: boolean;
}

/** Returns true when the account needs an email confirmation before first login. */
export async function signUp(input: SignUpInput): Promise<{ needsConfirmation: boolean }> {
  const { referral, source, attribution: campaign } = getAttribution();
  const country = await detectSignupCountry();
  const response = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: input.email.trim(),
      profile: { ...input, terms: true },
      country,
      referral,
      source,
      attribution: campaign,
      next: new URLSearchParams(window.location.search).get('next'),
    }),
  });
  const result = (await response.json()) as { error?: string };
  if (!response.ok) throw new Error(result.error || 'Couldn’t create your account. Please try again.');
  return { needsConfirmation: true };
}

function signupRedirectUrl(): string {
  const next = new URLSearchParams(window.location.search).get('next');
  return passwordRedirect(window.location.origin, true, next);
}

export async function resendSignupLink(email: string): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: { shouldCreateUser: false, emailRedirectTo: signupRedirectUrl() },
  });
  if (error) throw error;
}

const CODE_ERROR = 'That code is wrong or has expired. Check the latest email or request a new one.';

/** Verifies the code from the signup email in this tab; the session it creates can then set a password. */
export async function verifySignupCode(email: string, code: string): Promise<void> {
  const token = code.replace(/\D/g, '');
  if (token.length < 6 || token.length > 10) throw new Error('Enter the code from the email.');
  const { data, error } = await supabase.auth.verifyOtp({ email: email.trim(), token, type: 'email' });
  if (error) {
    if (error.code === 'otp_expired' || /expired|invalid/i.test(error.message)) throw new Error(CODE_ERROR);
    throw error;
  }
  if (!data.session?.user.email_confirmed_at) throw new Error(CODE_ERROR);
}

export async function resendConfirmation(email: string): Promise<void> {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.trim(),
    options: { emailRedirectTo: `${window.location.origin}/login?confirmed=1` },
  });
  if (error) throw error;
}

export async function signInWithGoogle(
  idToken?: string,
  nonce?: string,
  intent: 'login' | 'signup' = 'login',
  profile?: SignupProfile,
): Promise<{ isNewUser: boolean; missingAccount?: { email: string; name: string } }> {
  // Hosted OAuth can create an unknown user before we can ask for signup consent.
  if (!idToken) {
    throw new Error('Google sign-in could not load. Please try again.');
  }
  if (intent === 'login') {
    const response = await fetch('/api/auth/google/account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: idToken, nonce }),
    });
    const account = (await response.json()) as {
      exists?: boolean;
      email?: string;
      name?: string;
      error?: string;
    };
    if (!response.ok || typeof account.exists !== 'boolean' || !account.email)
      throw new Error(account.error || 'Couldn’t check your account. Please try again.');
    if (!account.exists)
      return { isNewUser: false, missingAccount: { email: account.email, name: account.name || '' } };
  }
  const result =
    intent === 'signup'
      ? await (async () => {
          const response = await fetch('/api/auth/google/signup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              credential: idToken,
              nonce,
              profile,
              country: await detectSignupCountry(),
            }),
          });
          const session = (await response.json()) as Session & {
            msg?: string;
            message?: string;
            error?: string;
          };
          if (!response.ok || !session.access_token || !session.refresh_token)
            throw new Error(
              session.msg ||
                session.message ||
                session.error ||
                'Couldn’t create your account. Please try again.',
            );
          return supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          });
        })()
      : await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken, nonce });
  const { data, error } = result;
  if (error) throw error;
  const createdAt = data.user?.created_at ? new Date(data.user.created_at).getTime() : 0;
  const isNewUser = Date.now() - createdAt < 60_000;
  if (isNewUser && data.user) {
    try {
      await saveGoogleAttribution(data.user);
    } catch {
      // The session is already established; attribution can retry on the next load.
    }
  }
  return { isNewUser };
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut({ scope: 'local' });
}

/** Revokes every session for this user, on all devices. */
export async function signOutEverywhere(): Promise<void> {
  const { error } = await supabase.auth.signOut({ scope: 'global' });
  if (error) throw error;
}

export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: passwordRedirect(window.location.origin, false),
  });
  // Keep the response neutral for unknown accounts; delivery/configuration failures still surface.
  if (error && !['user_not_found'].includes(error.code || '')) throw error;
}

export async function getAuthCapabilities(): Promise<{ googleEnabled: boolean; emailEnabled: boolean }> {
  const response = await databaseFetch(`${config.supabaseUrl}/auth/v1/settings`, {
    headers: { apikey: config.supabaseAnonKey },
  });
  if (!response.ok) throw new Error('Couldn’t check sign-in options. Please try again.');
  const data = await response.json();
  return { googleEnabled: data.external?.google === true, emailEnabled: data.external?.email === true };
}

/** A normal existing login is not proof that this page received a verification/recovery link. */
export async function completePasswordLink(
  setup: boolean,
  link: PasswordLink = passwordLinkContext,
): Promise<void> {
  if (link.kind === 'error') throw new Error(link.message);
  if (link.kind === 'none')
    throw new Error('Open the link from your email to continue, or request a new one.');
  const accepted = setup ? ['email', 'signup', 'magiclink'] : ['recovery'];
  if (!accepted.includes(link.type))
    throw new Error('This link is not valid for this password step. Request a new link.');
  if (link.kind === 'token_hash') {
    const { error } = await supabase.auth.verifyOtp({ token_hash: link.tokenHash, type: link.type });
    if (error) throw error;
  }
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (link.kind === 'implicit' && data.session?.access_token !== link.accessToken)
    throw new Error('This link is invalid or has already been used. Request a new one.');
  if (!data.session?.user.email_confirmed_at)
    throw new Error('This link is invalid or has already been used. Request a new one.');
}

export async function updatePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

/** Confirms the current password by signing in again before changing it. */
export async function changePassword(
  email: string,
  currentPassword: string | null,
  nextPassword: string,
): Promise<void> {
  if (currentPassword !== null) {
    const { error } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
    if (error) throw new Error('Your current password is incorrect.');
  }
  await updatePassword(nextPassword);
}

export async function updateUserMetadata(data: Record<string, unknown>): Promise<void> {
  const { error } = await supabase.auth.updateUser({ data });
  if (error) throw error;
}
